/**
 * useImagePromptJob
 *
 * Runs a Fooocus image-prompt / face-swap generation and exposes a small,
 * stable state surface (isGenerating / result / error / ...) shared by
 * ImagePromptTab and FaceSwapTab.
 *
 * Why synchronous (2026-06-23):
 *   Fooocus-API's `/v2/generation/image-prompt` with `async_process: true`
 *   returns a job_id whose job never reaches a terminal state — `query-job`
 *   polls forever (confirmed in the desktop backend log: 3+ minutes of GET
 *   query-job, no completion). The SYNCHRONOUS path returns the images directly
 *   in the POST response and is exactly what the other Fooocus edit operations
 *   (upscale, inpaint, enhance, remove-bg) already use successfully. So we run
 *   it synchronously and show a reassuring "this can take a moment" message
 *   instead of a progress bar.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { imageGenerationAPI } from '@/services/imageGenerationAPI';
import type { ImagePromptRequest, ImageResult } from '@/types';
import { handleError } from '@/utils/errorHandler';
import { apiFailureError } from '@/utils/httpUtils';

export interface UseImagePromptJob {
  isGenerating: boolean;
  /** Always 0 (sync generation reports no incremental progress). */
  progress: number;
  /** Reassuring status line shown under the spinner while generating. */
  message: string;
  result: ImageResult[] | null;
  error: string | null;
  processingTime: number | undefined;
  run: (request: ImagePromptRequest) => Promise<void>;
  reset: () => void;
}

export function useImagePromptJob(): UseImagePromptJob {
  const [isGenerating, setIsGenerating] = useState(false);
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<ImageResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [processingTime, setProcessingTime] = useState<number | undefined>();
  const cancelledRef = useRef(false);

  // Ignore the in-flight result if the form unmounts mid-request (e.g. the user
  // switches Image Edit operation or model while a generation is running).
  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const reset = useCallback(() => {
    setIsGenerating(false);
    setMessage('');
    setResult(null);
    setError(null);
    setProcessingTime(undefined);
  }, []);

  const run = useCallback(async (request: ImagePromptRequest) => {
    cancelledRef.current = false;
    setIsGenerating(true);
    setError(null);
    setResult(null);
    setProcessingTime(undefined);
    setMessage('');

    const startTime = Date.now();

    try {
      // Synchronous: Fooocus returns the generated images directly. Cold start
      // (Fooocus auto-start + model load) can add a few seconds on first run.
      const response = await imageGenerationAPI.imagePromptGenerate({
        ...request,
        async_process: false,
      });

      if (cancelledRef.current) return;

      if (!response.success || !response.data) {
        // Keeps the HTTP status and the envelope's error.code on the thrown
        // error. This path is Fooocus-only today and cannot be refused for
        // money, so nothing here branches on them — but flattening a failed
        // ApiResponse into its message is how the signals get lost for good,
        // and the next caller to need them would find them already gone.
        throw apiFailureError(response, 'Failed to generate images');
      }

      const images = Array.isArray(response.data.images) ? response.data.images : [];
      if (images.length === 0) {
        throw new Error('No images were generated');
      }

      setResult(images);
      setProcessingTime(response.data.processing_time_seconds ?? (Date.now() - startTime) / 1000);
    } catch (err) {
      if (cancelledRef.current) return;
      setError(handleError(err, 'useImagePromptJob', 'Generation failed'));
    } finally {
      if (!cancelledRef.current) setIsGenerating(false);
    }
  }, []);

  return {
    isGenerating,
    progress: 0,
    message,
    result,
    error,
    processingTime,
    run,
    reset,
  };
}

export default useImagePromptJob;
